/**
 * SuspendDialog - asking a moderator how long, and why
 * -----------------------------------------------------
 * Suspending used to be one button that acted immediately, with a reason the
 * code wrote for itself ("Report: inappropriate_behaviour"). Two problems
 * with that, and the second is the serious one:
 *
 *   1. Every ban was permanent until somebody undid it by hand. Most real
 *      cases are "a week off for that", and a week later the reinstatement
 *      depends on a moderator remembering.
 *
 *   2. The person banned was shown a database value. Whatever is typed here
 *      is what they read, word for word, every time they try to log in - so
 *      it needs to be a sentence written by a person, to a person, who may
 *      well be sixteen and frightened.
 *
 * Hence: a length, and a message, both required, before anything happens.
 *
 * The presets exist because a free number box invites arithmetic nobody
 * needs, and "how many days is a fortnight" is not a decision worth making at
 * the moment you are dealing with a report. Custom is there for when the
 * presets are wrong.
 */

import { useState } from 'react';
import { Ban } from 'lucide-react';

import Modal from './Modal';
import { useI18n } from '../hooks/useI18n';

const PRESETS = [
  { days: 1, label: '1 day' },
  { days: 3, label: '3 days' },
  { days: 7, label: '1 week' },
  { days: 30, label: '1 month' },
];

export default function SuspendDialog({ open, person, onClose, onConfirm, busy }) {
  const { t } = useI18n();
  const [choice, setChoice] = useState(7); // days, or the string 'indefinite'
  const [custom, setCustom] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  if (!person) return null;

  const indefinite = choice === 'indefinite';
  const days = choice === 'custom' ? Number(custom) : choice;

  const confirm = () => {
    const message = reason.trim();
    if (!message) {
      setError(t('suspend.needReason'));
      return;
    }
    if (!indefinite && (!Number.isFinite(days) || days < 1 || days > 365)) {
      setError(t('suspend.badLength'));
      return;
    }
    setError('');
    onConfirm({ reason: message, indefinite, days: indefinite ? undefined : days });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('suspend.titleFor', {
        name: person.firstName || t('suspend.thisPerson'),
      })}
      actions={
        <>
          <button type="button" className="btn btn-ghost btn-inline" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-danger btn-inline" onClick={confirm} disabled={busy}>
            <Ban size={16} />
            {busy ? t('suspend.suspending') : indefinite ? t('suspend.indefinitely') : t('suspend.forDays', { days: days || '?' })}
          </button>
        </>
      }
    >
      <p className="text-muted">
        {t('suspend.signedOutEverywhere')}
      </p>

      <div className="field" style={{ marginTop: 'var(--space-sm)' }}>
        <span className="label">{t('suspend.howLong')}</span>
        <div className="row" style={{ gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
          {PRESETS.map((p) => (
            <button
              key={p.days}
              type="button"
              className={`btn btn-sm btn-inline ${choice === p.days ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setChoice(p.days)}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={`btn btn-sm btn-inline ${choice === 'custom' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setChoice('custom')}
          >
            {t('suspend.custom')}
          </button>
          <button
            type="button"
            className={`btn btn-sm btn-inline ${indefinite ? 'btn-danger' : 'btn-outline'}`}
            onClick={() => setChoice('indefinite')}
          >
            {t('suspend.indefinitely')}
          </button>
        </div>
      </div>

      {choice === 'custom' && (
        <div className="field">
          <label htmlFor="ban-days">{t('suspend.numberOfDays')}</label>
          <input
            id="ban-days"
            type="number"
            min="1"
            max="365"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="e.g. 14"
          />
        </div>
      )}

      <div className="field">
        <label htmlFor="ban-reason">{t('suspend.whyTheyRead')}</label>
        <textarea
          id="ban-reason"
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          placeholder={t('suspend.reasonPlaceholder')}
        />
        <p className="hint">
          {t('suspend.reasonHint')}
        </p>
      </div>

      {indefinite && (
        <div className="alert alert-warning">
          {t('suspend.indefiniteWarning')}
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
    </Modal>
  );
}
